import { useEffect, useMemo, useRef, useState } from "react";
import { LogOut, Link2, CreditCard, Coins, Lock, Settings2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import IntegrationModal from "@/components/IntegrationModal";
import PreferencesModal from "@/components/PreferencesModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";

function capitalise(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getGreetingKey(hour) {
  if (hour < 12) return "header.greeting.morning";
  if (hour < 19) return "header.greeting.afternoon";
  return "header.greeting.evening";
}

function formatDateLocalised(date, locale = "es-ES") {
  try {
    const formatted = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "short",
    }).format(date);
    return capitalise(formatted.replace(".", ""));
  } catch {
    return date.toDateString();
  }
}

function initialsFrom(user) {
  const name = (user?.given_name || user?.name || user?.email || "").trim();
  if (!name) return "SF";
  const parts = name.split(/\s+/);
  const first = parts[0]?.[0] || "";
  const second = parts.length > 1 ? parts[parts.length - 1][0] : (user?.family_name?.[0] || "");
  return (first + second).toUpperCase() || "SF";
}

function Avatar({ user, onClick, active }) {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);
  const picture = user?.picture;
  const showFallback = !picture || imgError;
  const initials = initialsFrom(user);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="header-avatar"
      aria-expanded={active}
      aria-label={t("header.avatar_aria")}
      className={`
        relative h-12 w-12 sm:h-[52px] sm:w-[52px] rounded-full overflow-hidden
        ring-1 transition-all duration-300
        ${active
          ? "ring-[rgba(0,229,255,0.7)] shadow-[0_0_18px_rgba(0,229,255,0.45)]"
          : "ring-[rgba(0,229,255,0.4)] shadow-[0_0_12px_rgba(0,229,255,0.25)] hover:ring-[rgba(0,229,255,0.6)] hover:shadow-[0_0_16px_rgba(0,229,255,0.35)]"}
        bg-[#111827]
        flex items-center justify-center
        focus:outline-none focus-visible:ring-[rgba(0,229,255,0.8)]
      `}
    >
      {showFallback ? (
        <span
          className="font-mono text-sm font-semibold tracking-wider text-[#00E5FF]"
          data-testid="header-avatar-fallback"
        >
          {initials}
        </span>
      ) : (
        <img
          src={picture}
          alt={user?.name || "Avatar"}
          loading="eager"
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
          className="h-full w-full object-cover"
          data-testid="header-avatar-img"
        />
      )}
    </button>
  );
}

function UserMenu({ user, onClose, onLogout, onOpenIntegration, onOpenBilling, onOpenPreferences, isElite, t }) {
  const ref = useRef(null);

  // Close when clicking outside or pressing ESC.
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const tier = user?.subscription?.tier || "free";
  const balance = user?.nexa_tokens?.balance ?? 0;
  const unlimited = !!user?.nexa_tokens?.unlimited;
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

  return (
    <div
      ref={ref}
      data-testid="header-user-menu"
      className="
        absolute top-[calc(100%+10px)] right-0 z-[30]
        w-[280px] rounded-2xl
        bg-[rgba(10,14,26,0.96)] backdrop-blur-xl
        border border-[rgba(0,229,255,0.18)]
        shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7),0_0_40px_-10px_rgba(0,229,255,0.35)]
        py-3 animate-fade-in-up
      "
    >
      <div className="px-4 pb-3 border-b border-white/[0.06]">
        <p className="text-[13.5px] font-semibold text-[#E5E7EB] truncate" data-testid="menu-user-name">
          {user?.name || "—"}
        </p>
        <p className="text-[11.5px] text-[#9CA3AF] truncate mt-0.5" data-testid="menu-user-email">
          {user?.email || ""}
        </p>
        <div className="mt-2 flex items-center gap-2 text-[10.5px]">
          <span
            data-testid="menu-tier-badge"
            className={`px-2 py-0.5 rounded-full uppercase tracking-[0.16em] font-semibold ${
              tier === "elite"
                ? "bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF]"
                : tier === "free"
                  ? "bg-white/[0.04] border border-white/[0.1] text-[#9CA3AF]"
                  : "bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.35)] text-[#00E5FF]"
            }`}
          >
            {tierLabel}
          </span>
          <span
            data-testid="menu-balance"
            className="inline-flex items-center gap-1 text-[#9CA3AF] font-mono"
          >
            <Coins className="h-3 w-3 text-[#00E5FF]" strokeWidth={2} />
            {unlimited ? "∞" : balance}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenBilling}
        data-testid="menu-billing-button"
        className="w-full flex items-center gap-2.5 px-4 py-2.5 mt-1 text-[13px] text-[#E5E7EB] hover:bg-white/[0.04] transition-colors duration-150 text-left"
      >
        <CreditCard className="h-4 w-4 text-[#9CA3AF]" strokeWidth={1.75} />
        <span className="flex-1">{t("dropdown.subscription")}</span>
      </button>

      <button
        type="button"
        onClick={onOpenIntegration}
        data-testid="menu-integration-button"
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-[#E5E7EB] hover:bg-white/[0.04] transition-colors duration-150 text-left"
      >
        <Link2 className="h-4 w-4 text-[#9CA3AF]" strokeWidth={1.75} />
        <span className="flex-1">{t("dropdown.integration")}</span>
        {!isElite ? (
          <span
            data-testid="menu-integration-locked"
            className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[#9CA3AF]"
          >
            <Lock className="h-2.5 w-2.5" strokeWidth={2} />
            Elite
          </span>
        ) : null}
      </button>

      <button
        type="button"
        onClick={onOpenPreferences}
        data-testid="menu-preferences-button"
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-[#E5E7EB] hover:bg-white/[0.04] transition-colors duration-150 text-left"
      >
        <Settings2 className="h-4 w-4 text-[#9CA3AF]" strokeWidth={1.75} />
        <span className="flex-1">{t("dropdown.preferences")}</span>
      </button>

      <div className="my-1 mx-3 border-t border-white/[0.04]" aria-hidden="true" />

      <LanguageSwitcher />

      <div className="my-1 mx-3 border-t border-white/[0.04]" aria-hidden="true" />

      <button
        type="button"
        onClick={onLogout}
        data-testid="menu-logout-button"
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-[#E5E7EB] hover:bg-white/[0.04] transition-colors duration-150 text-left"
      >
        <LogOut className="h-4 w-4 text-[#9CA3AF]" strokeWidth={1.75} />
        {t("dropdown.logout")}
      </button>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [menuOpen, setMenuOpen] = useState(false);
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const localeMap = { es: "es-ES", en: "en-US", pt: "pt-BR", hi: "hi-IN" };
  const locale = localeMap[(i18n.language || "es").split("-")[0]] || "es-ES";
  const greeting = useMemo(() => t(getGreetingKey(now.getHours())), [now, t]);
  const dateLabel = useMemo(() => formatDateLocalised(now, locale), [now, locale]);

  const displayName = user?.given_name || user?.name?.split(" ")?.[0] || "Fernando";
  const prefix = user?.prefix || "Sr.";
  const tier = user?.subscription?.tier || "free";

  return (
    <header
      className="relative z-[2] flex items-start justify-between gap-4 px-5 pt-7 pb-4"
      data-testid="header"
    >
      <div className="flex flex-col min-w-0">
        <span className="text-[12px] tracking-[0.04em] text-[#9CA3AF]" data-testid="header-date">
          {dateLabel}
        </span>
        <h1
          className="mt-1 text-[22px] sm:text-[24px] font-semibold leading-tight text-[#E5E7EB]"
          data-testid="header-greeting"
        >
          {greeting}
        </h1>
      </div>

      <div className="relative flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p
            className="text-[15px] font-semibold leading-tight text-[#E5E7EB]"
            data-testid="header-user-name"
          >
            {prefix} {displayName}
          </p>
          <p
            className="text-[11px] tracking-wide text-[#00E5FF]/80 mt-0.5"
            data-testid="header-user-subtitle"
          >
            {t("header.subtitle")}
          </p>
        </div>
        <Avatar
          user={user}
          active={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        />
        {menuOpen ? (
          <UserMenu
            user={user}
            t={t}
            isElite={tier === "elite"}
            onClose={() => setMenuOpen(false)}
            onOpenBilling={() => {
              setMenuOpen(false);
              navigate("/billing");
            }}
            onOpenIntegration={() => {
              setMenuOpen(false);
              setIntegrationOpen(true);
            }}
            onOpenPreferences={() => {
              setMenuOpen(false);
              setPreferencesOpen(true);
            }}
            onLogout={async () => {
              setMenuOpen(false);
              await logout();
              window.location.replace("/login");
            }}
          />
        ) : null}
      </div>

      <IntegrationModal
        open={integrationOpen}
        onClose={() => setIntegrationOpen(false)}
      />
      <PreferencesModal
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </header>
  );
}
